import React from 'react';
import { useTranslation } from 'react-i18next';

import PageLoadingSpinner from '_components/PageLoadingSpinner/PageLoadingSpinner';
const BackupOneTimePasswordForm = React.lazy(() => import('Login/components/LoginForm/BackupOneTimePasswordForm'));
const CustomIdForm = React.lazy(() => import('Login/components/LoginForm/CustomIdForm'));
const LoginCodeForm = React.lazy(() => import('Login/components/LoginForm/LoginCodeForm'));
const OneTimePasswordForm = React.lazy(() => import('Login/components/LoginForm/OneTimePasswordForm'));
const PasswordForm = React.lazy(() => import('Login/components/LoginForm/PasswordForm'));

import type { CustomIdField, LoginRequest } from '@/types/auth';

type LoginFormRendererProps = {
  currentStatusCode: string;
  loginRequest: LoginRequest;
  customIdField: CustomIdField | null;
  hasError: boolean;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onSubmit: () => void;
};

/**
 * Renders the appropriate login form or loading state based on the current status code returned by the API.
 */
export function LoginFormRenderer({
  currentStatusCode,
  customIdField,
  hasError,
  loginRequest,
  onChange,
  onSubmit
}: LoginFormRendererProps): React.ReactElement {
  const { t } = useTranslation();

  const commonProps = {hasError: hasError, onChange: onChange, onSubmit: onSubmit}

  switch (currentStatusCode) {
    // Loading states. These occur during and after authentication, while fetching additional data from the API.
    case 'PL':  // Waiting for the login request to complete
      return <PageLoadingSpinner label="Logging in" />;
    case 'AC':  // Authenticated with a login code.
    case 'AP':  // Authenticated with a login code and password.
    case 'AV':  // Authenticated with a login code, password, and 2FA.
      return <PageLoadingSpinner label="Fetching survey items" />;

    // Login code form states
    case 'II':  // Invalid input
    case 'IL':  // Invalid login code
    case 'EL':  // Expired login code
    case 'ML':  // Missing login code
      return <LoginCodeForm {...commonProps} value={loginRequest.loginCodeValue} />;

    // Custom ID form states
    case 'IC':  // Invalid custom ID
    case 'MC':  // Missing custom ID
      return <CustomIdForm {...commonProps} customIdField={customIdField} value={loginRequest.customIdValue} />;

    // Password form states
    case 'IP': // Invalid password
    case 'MP': // Missing password
      return <PasswordForm {...commonProps} value={loginRequest.password} />;

    // 2FA form states
    case 'IO':  // Invalid OTP
    case 'MO':  // Missing OTP
      return <OneTimePasswordForm {...commonProps} value={loginRequest.oneTimePassword} />;
    case 'IB':  // Invalid backup OTP
      return <BackupOneTimePasswordForm {...commonProps} value={loginRequest.backupOneTimePassword} />;

    // 2FA misconfiguration
    case 'NV':
      return <p>OTP submitted but 2-factor authentication is not configured for this user.</p>;
    case 'NB':
      return <p>Backup token submitted but no backup tokens are configured for this user.</p>;

    // Edge cases — email login not used but handled gracefully
    case 'ME':
      return <p>Missing email address.</p>;
    case 'IE':
      return <p>Email address not found.</p>;

    // API failure after login
    case 'ER':
      return <p>An error occurred while fetching data after login. Please try again.</p>;

    default:
      console.log(`${currentStatusCode} is not a recognized login status code.`);
      throw new Error(t("login.statusCodeErrorMessage"));
  }
}